import { useCallback, useEffect, useRef, useState } from 'react';
import { useConnection } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress } from '@solana/spl-token';
import { CWY_MINT_ADDRESS } from '../config/cwyConfig';
import { getWalletCwyBalance } from '../utils/cwyTokenBalance';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';

export function useWalletCwyBalance(walletAddress: string | null | undefined, enabled = true) {
  const { connection } = useConnection();
  const [balance, setBalance] = useState<number | null>(null);
  const [ataExists, setAtaExists] = useState(false);
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const solListenerRef = useRef<number | null>(null);
  const ataListenerRef = useRef<number | null>(null);
  // debounce timer for subscription-triggered refreshes
  const ataDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // silent=true → update balance in background without showing the loading spinner
  const refresh = useCallback(async (silent = false) => {
    if (!enabled || !walletAddress || !CWY_MINT_ADDRESS) {
      setBalance(null);
      setAtaExists(false);
      setSolBalance(null);
      return;
    }

    if (!silent) setLoading(true);
    setError(null);

    try {
      const owner = new PublicKey(walletAddress);
      const info = await getWalletCwyBalance(connection, owner);
      setBalance(info.balance);
      setAtaExists(info.ataExists);
      setSolBalance(info.solBalance);
    } catch {
      setBalance(0);
      setAtaExists(false);
      setSolBalance(0);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [connection, enabled, walletAddress]);

  // initial fetch + delayed retries to catch RPC lag on page load
  useEffect(() => {
    void refresh();
    const t1 = setTimeout(() => void refresh(true), 1500);
    const t2 = setTimeout(() => void refresh(true), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [refresh]);

  // real-time on-chain subscriptions
  useEffect(() => {
    if (!enabled || !walletAddress || !CWY_MINT_ADDRESS) return;

    let cancelled = false;

    const setupListeners = async () => {
      try {
        const owner = new PublicKey(walletAddress);

        // SOL balance — update directly from notification data, no full fetch needed
        const solId = connection.onAccountChange(owner, (accountInfo) => {
          setSolBalance(accountInfo.lamports / LAMPORTS_PER_SOL);
        });
        solListenerRef.current = solId;

        if (!CWY_MINT_ADDRESS) return;
        const mint = new PublicKey(CWY_MINT_ADDRESS);
        const ata = await getAssociatedTokenAddress(mint, owner);

        if (cancelled) {
          void connection.removeAccountChangeListener(solId);
          return;
        }

        // CWY ATA — debounce so rapid slot notifications don't trigger multiple
        // refresh cycles and cause the balance animation to fire repeatedly
        const ataId = connection.onAccountChange(ata, () => {
          if (ataDebounceRef.current) clearTimeout(ataDebounceRef.current);
          ataDebounceRef.current = setTimeout(() => {
            void refresh(true); // silent — no loading flicker
          }, 600);
        });
        ataListenerRef.current = ataId;
      } catch {
        // ignore subscription errors — polled value is still valid
      }
    };

    void setupListeners();

    return () => {
      cancelled = true;
      if (ataDebounceRef.current) clearTimeout(ataDebounceRef.current);
      if (solListenerRef.current !== null) {
        void connection.removeAccountChangeListener(solListenerRef.current);
        solListenerRef.current = null;
      }
      if (ataListenerRef.current !== null) {
        void connection.removeAccountChangeListener(ataListenerRef.current);
        ataListenerRef.current = null;
      }
    };
  }, [connection, enabled, walletAddress, refresh]);

  return { balance, ataExists, solBalance, loading, error, refresh };
};

